// lib/logicalCanvas/policyEngine.js
/**
 * Logical Canvas Policy Engine
 * 
 * Resolves effective policy from:
 * 1. Upstream policy (from central governance - simulated for now)
 * 2. Local selection (user preferences in localStorage)
 * 3. Registry defaults (fallback)
 */

import { getAllCapabilities } from "./pluginRegistry.js";

/**
 * Sample upstream policy (hard-coded for sprint v0)
 * In production, this would come from an API endpoint
 * 
 * Policy structure:
 * {
 *   [capabilityId]: {
 *     enabled: boolean,
 *     mode: "read-only" | "read-write" | "disabled",
 *     locked: boolean  // if true, user cannot change this
 *     source: "upstream" | "local" | "default"
 *   }
 * }
 */
export const SAMPLE_UPSTREAM_POLICY = {
  // Lock invoices to read-only (enforced from upstream)
  "main_crm.invoices": {
    enabled: true,
    mode: "read-only",
    locked: true,
    source: "upstream",
  },
  // Lock security access control (disabled and locked)
  "security.access_control": {
    enabled: false,
    mode: "disabled",
    locked: true,
    source: "upstream",
  },
  // Sales forecasting allowed but read-only
  "sales.forecasting": {
    enabled: true,
    mode: "read-only",
    locked: false,
    source: "upstream",
  },
  // Delta rollback explicitly disabled
  "deltas.rollback": {
    enabled: false,
    mode: "disabled",
    locked: true,
    source: "upstream",
  },
};

/**
 * Resolve effective policy for all capabilities
 * 
 * Resolution order:
 * 1. If upstream.locked === true -> use upstream (user cannot override)
 * 2. If localSelection has preference -> use local
 * 3. If upstream has policy (but not locked) -> use upstream
 * 4. Else use registry default
 * 
 * @param {object} upstreamPolicy - Policy from upstream governance
 * @param {object} localSelection - User's local preferences from localStorage
 * @param {Map} registry - Capability registry
 * @returns {object} effectivePolicy - Resolved policy map
 */
export function resolveEffectivePolicy(
  upstreamPolicy = {},
  localSelection = {},
  registry = null
) {
  const capMap = registry || getAllCapabilities();
  const effectivePolicy = {};

  capMap.forEach((capability, capId) => {
    const upstream = upstreamPolicy[capId];
    const local = localSelection[capId];

    // Rule 1: If upstream is locked, it wins unconditionally
    if (upstream && upstream.locked) {
      effectivePolicy[capId] = {
        enabled: upstream.enabled ?? false,
        mode: upstream.mode ?? "disabled",
        locked: true,
        source: "upstream",
      };
      return;
    }

    // Rule 2: If local selection exists, use it (only if not locked)
    if (local && local.enabled !== undefined) {
      effectivePolicy[capId] = {
        enabled: local.enabled,
        mode: local.mode || capability.accessDefault,
        locked: false,
        source: "local",
      };
      return;
    }

    // Rule 3: If upstream has preference (but not locked), use it
    if (upstream && upstream.enabled !== undefined) {
      effectivePolicy[capId] = {
        enabled: upstream.enabled,
        mode: upstream.mode || capability.accessDefault,
        locked: false,
        source: "upstream",
      };
      return;
    }

    // Rule 4: Fallback to registry default
    const defaultEnabled = capability.accessDefault !== "disabled";
    effectivePolicy[capId] = {
      enabled: defaultEnabled,
      mode: capability.accessDefault,
      locked: false,
      source: "default",
    };
  });

  return effectivePolicy;
}

/**
 * Get upstream policy (for now, returns hard-coded sample)
 * In production, this would fetch from API
 * 
 * @returns {Promise<object>}
 */
export async function fetchUpstreamPolicy() {
  // Simulate async API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(SAMPLE_UPSTREAM_POLICY);
    }, 100);
  });
}

/**
 * Check if a capability is allowed to be toggled by user
 * @param {string} capabilityId 
 * @param {object} effectivePolicy 
 * @returns {boolean}
 */
export function isCapabilityEditable(capabilityId, effectivePolicy) {
  const policy = effectivePolicy[capabilityId];
  return policy ? !policy.locked : true;
}

/**
 * Validate that a mode change is allowed
 * @param {string} capabilityId 
 * @param {string} newMode 
 * @param {Map} registry 
 * @returns {boolean}
 */
export function isModeAllowed(capabilityId, newMode, registry = null) {
  const capMap = registry || getAllCapabilities();
  const capability = capMap.get(capabilityId);
  if (!capability) return false;
  return capability.modesAllowed.includes(newMode);
}
